import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, Target, Eye, Shield, Check, Lock, 
  ChevronRight, CheckCircle2, Circle, Play, Pause, 
  Volume2, RotateCcw, Headphones, Sparkles, MessageCircle, 
  Crown, Heart, Flame, ShieldOff, Zap, Moon, Settings2, Share2
} from 'lucide-react';
import { AudioEngine } from '../../components/AudioEngine';
import SessionModeSelector from '../../components/AudioEngine/SessionModeSelector';
import { ShareModal, ShareButton } from '../../components/ShareModal';
import { trackActivity } from '../../components/ReclaimAssistant';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NetworkerChallengesPage = () => {
  const navigate = useNavigate();
  const { challengeId, dayNumber } = useParams();
  const [challenges, setChallenges] = useState([]);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [taskCompleted, setTaskCompleted] = useState(false);
  
  // Audio state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentLine, setCurrentLine] = useState(-1);
  const [audioPhase, setAudioPhase] = useState('idle'); // idle, playing, closing, complete
  const timerRef = useRef(null);
  const lineIndexRef = useRef(0);
  
  // Audio Engine state
  const [showAudioEngine, setShowAudioEngine] = useState(false);
  const [sessionConfig, setSessionConfig] = useState(null);
  const [selectedMode, setSelectedMode] = useState('flow');
  const [showModeSelector, setShowModeSelector] = useState(false);
  
  // Share state
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareType, setShareType] = useState('progress');
  const [shareData, setShareData] = useState(null);

  const iconMap = {
    'eye': Eye,
    'shield': Shield,
    'target': Target,
    'message-circle': MessageCircle,
    'heart': Heart,
    'sparkles': Sparkles,
    'flame': Flame,
    'shield-off': ShieldOff,
    'crown': Crown
  };

  // Check if user has premium access (inner circle member or premium tier)
  const [userHasPremium, setUserHasPremium] = useState(false);

  useEffect(() => {
    fetchChallenges();
    checkPremiumAccess();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const checkPremiumAccess = async () => {
    try {
      const token = localStorage.getItem('reclaim_token');
      const res = await fetch(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      // Premium access if inner_circle_member or premium tier
      setUserHasPremium(
        data.inner_circle_member === true || 
        data.membership_tier === 'premium' ||
        data.membership_tier === 'admin'
      );
    } catch (err) {
      console.error('Error checking premium access:', err);
    }
  };

  useEffect(() => {
    if (challengeId && challenges.length > 0) {
      const challenge = challenges.find(c => c.id === challengeId);
      if (challenge) {
        setSelectedChallenge(challenge);
        if (dayNumber) {
          const day = challenge.days.find(d => d.day === parseInt(dayNumber));
          if (day) setSelectedDay(day);
        }
      }
    }
  }, [challengeId, dayNumber, challenges]);

  const fetchChallenges = async () => {
    try {
      const token = localStorage.getItem('reclaim_token');
      const res = await fetch(`${API_URL}/api/networker/challenges`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setChallenges(data.challenges || []);
    } catch (err) {
      console.error('Error fetching challenges:', err);
    } finally {
      setLoading(false);
    }
  };

  const startChallenge = async (challengeId) => {
    try {
      // Track challenge start for smart suggestions
      trackActivity('challenge_started', { challengeId });
      
      const token = localStorage.getItem('reclaim_token');
      await fetch(`${API_URL}/api/networker/challenges/${challengeId}/start`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchChallenges();
    } catch (err) {
      console.error('Error starting challenge:', err);
    }
  };

  // Launch full Audio Engine session
  const launchAudioEngine = async () => {
    if (!selectedChallenge || !selectedDay) return;
    
    try {
      const token = localStorage.getItem('reclaim_token');
      const res = await fetch(
        `${API_URL}/api/audio-engine/challenge-session/${selectedChallenge.id}/day/${selectedDay.day}?mode=${selectedMode}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      const data = await res.json();
      
      if (data.success) {
        setSessionConfig(data.session);
        setShowAudioEngine(true);
      }
    } catch (err) {
      console.error('Error launching audio engine:', err);
      // Fallback to simple playback
      playAudio();
    }
  };

  const completeDay = async () => {
    try {
      const token = localStorage.getItem('reclaim_token');
      await fetch(`${API_URL}/api/networker/challenges/${selectedChallenge.id}/day/${selectedDay.day}/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          task_completed: taskCompleted
        })
      });
      
      // Show share modal for completion
      const progress = selectedChallenge.user_progress;
      const completedCount = (progress?.completed_days?.length || 0) + 1;
      const isLastDay = selectedDay.day === selectedChallenge.duration_days;
      
      setShareData({
        challengeName: selectedChallenge.name,
        dayNumber: selectedDay.day,
        dayTitle: selectedDay.title,
        theme: selectedDay.theme,
        totalDays: selectedChallenge.duration_days,
        completedDays: completedCount
      });
      setShareType(isLastDay ? 'achievement' : 'completion');
      setShowShareModal(true);
      
      // Reset and refresh
      setTaskCompleted(false);
      stopAudio();
      fetchChallenges();
    } catch (err) {
      console.error('Error completing day:', err);
    }
  };

  // Share handlers
  const openShareProgress = () => {
    if (!selectedChallenge || !selectedDay) return;
    
    const progress = selectedChallenge.user_progress;
    setShareData({
      challengeName: selectedChallenge.name,
      dayNumber: selectedDay.day,
      dayTitle: selectedDay.title,
      theme: selectedDay.theme,
      totalDays: selectedChallenge.duration_days,
      completedDays: progress?.completed_days?.length || 0
    });
    setShareType('progress');
    setShowShareModal(true);
  };

  const openShareInvite = () => {
    if (!selectedChallenge) return;
    
    setShareData({
      challengeName: selectedChallenge.name,
      totalDays: selectedChallenge.duration_days
    });
    setShareType('invite');
    setShowShareModal(true);
  };

  // Audio playback logic - simulates reading affirmations aloud
  const playAudio = () => {
    if (!selectedDay?.audio_script) return;
    
    setIsPlaying(true);
    setAudioPhase('playing');
    lineIndexRef.current = 0;
    setCurrentLine(0);
    playNextLine();
  };

  const playNextLine = () => {
    const script = selectedDay.audio_script;
    
    if (lineIndexRef.current >= script.length) {
      // Move to closing
      setAudioPhase('closing');
      timerRef.current = setTimeout(() => {
        setAudioPhase('complete');
        setIsPlaying(false);
      }, 4000);
      return;
    }
    
    setCurrentLine(lineIndexRef.current);
    
    // Each line takes ~4 seconds (read + pause)
    timerRef.current = setTimeout(() => {
      lineIndexRef.current++;
      playNextLine();
    }, 4000);
  };

  const stopAudio = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setIsPlaying(false);
    setCurrentLine(-1);
    setAudioPhase('idle');
    lineIndexRef.current = 0;
  };

  const resetAudio = () => {
    stopAudio();
    setTimeout(() => playAudio(), 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050402] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  // Day detail view - NEW DESIGN matching user's requirements
  if (selectedDay) {
    return (
      <div className="min-h-screen bg-[#050402]">
        <div className="px-6 pt-12 pb-6">
          <div className="flex items-center justify-between mb-6">
            <button 
              onClick={() => { setSelectedDay(null); stopAudio(); }}
              className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
              data-testid="back-to-challenge-btn"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back</span>
            </button>
            
            <ShareButton 
              onClick={openShareProgress} 
              variant="icon" 
            />
          </div>
          
          <div className="flex items-center gap-2 text-amber-400 text-sm mb-2">
            <span>Day {selectedDay.day}</span>
            <span>•</span>
            <span>{selectedChallenge.name}</span>
          </div>
          <h1 className="text-2xl font-bold text-white mb-1" data-testid="day-title">
            {selectedDay.title}
          </h1>
          <p className="text-lg text-amber-300 italic">"{selectedDay.theme}"</p>
        </div>

        <div className="px-6 space-y-6 pb-24">
          {/* Insight Card */}
          <div className="bg-gradient-to-br from-amber-500/10 to-amber-700/10 rounded-2xl p-5 border border-amber-500/20">
            <div className="flex items-center gap-2 text-amber-400 mb-3">
              <Sparkles className="w-5 h-5" />
              <h3 className="font-medium">Insight</h3>
            </div>
            <p className="text-white/90 text-lg leading-relaxed">
              {selectedDay.insight}
            </p>
          </div>

          {/* Action Card */}
          <div className="bg-white/5 rounded-2xl p-5 border border-white/10">
            <div className="flex items-center gap-2 text-amber-400 mb-3">
              <Target className="w-5 h-5" />
              <h3 className="font-medium">Today's Action</h3>
            </div>
            <p className="text-white/80 mb-4">{selectedDay.action}</p>
            <button
              onClick={() => setTaskCompleted(!taskCompleted)}
              className={`flex items-center gap-3 w-full p-4 rounded-xl border transition-all ${
                taskCompleted 
                  ? 'bg-emerald-500/20 border-emerald-500/50' 
                  : 'bg-white/5 border-white/10 hover:border-white/30'
              }`}
              data-testid="task-complete-btn"
            >
              {taskCompleted ? (
                <CheckCircle2 className="w-6 h-6 text-emerald-400" />
              ) : (
                <Circle className="w-6 h-6 text-slate-400" />
              )}
              <span className={taskCompleted ? 'text-emerald-400 font-medium' : 'text-white/70'}>
                {taskCompleted ? 'Action Completed!' : 'Mark as complete'}
              </span>
            </button>
          </div>

          {/* Audio Section - Main Feature */}
          <div className="bg-gradient-to-br from-amber-900/30 to-amber-700/20 rounded-2xl p-5 border border-amber-500/30">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 text-amber-300/85">
                <Headphones className="w-5 h-5" />
                <h3 className="font-medium">Audio Session</h3>
              </div>
              <button
                onClick={() => setShowModeSelector(!showModeSelector)}
                className="text-xs text-amber-400 bg-amber-500/20 px-3 py-1 rounded-full 
                         flex items-center gap-1 hover:bg-amber-500/30 transition-colors"
              >
                <Settings2 className="w-3 h-3" />
                {selectedMode.replace('_', ' ')}
              </button>
            </div>

            {/* Mode Selector */}
            {showModeSelector && (
              <div className="mb-4 p-3 bg-black/20 rounded-xl">
                <SessionModeSelector
                  selectedMode={selectedMode}
                  onModeSelect={(mode) => {
                    setSelectedMode(mode);
                    setShowModeSelector(false);
                  }}
                  compact
                />
              </div>
            )}

            {/* Full Session Launch */}
            <button
              onClick={launchAudioEngine}
              className="w-full py-4 mb-4 rounded-xl bg-gradient-to-r from-amber-500 to-amber-700 
                       text-white font-medium flex items-center justify-center gap-2
                       hover:opacity-90 transition-opacity shadow-lg"
              data-testid="launch-full-session-btn"
            >
              <Headphones className="w-5 h-5" />
              Launch Full Session
            </button>

            <div className="text-center text-xs text-amber-400/60 mb-4">
              5-layer audio: Ambient + Binaural + Subliminal + Voice + Guidance
            </div>

            {/* Quick Play - Simple Affirmation Display */}
            <div className="border-t border-amber-500/20 pt-4">
              <p className="text-xs text-amber-400 mb-3 text-center">Or quick preview:</p>
              
              <div className="flex items-center justify-center gap-4 mb-4">
                {audioPhase === 'complete' ? (
                  <button
                    onClick={resetAudio}
                    className="w-12 h-12 rounded-full bg-white/10 
                             flex items-center justify-center hover:bg-white/20 transition-colors"
                    data-testid="replay-audio-btn"
                  >
                    <RotateCcw className="w-5 h-5 text-white" />
                  </button>
                ) : (
                  <button
                    onClick={isPlaying ? stopAudio : playAudio}
                    className={`w-12 h-12 rounded-full flex items-center justify-center 
                             hover:scale-105 transition-transform ${
                               isPlaying 
                                 ? 'bg-amber-500/50' 
                                 : 'bg-white/10 hover:bg-white/20'
                             }`}
                    data-testid="play-audio-btn"
                  >
                    {isPlaying ? (
                      <Pause className="w-5 h-5 text-white" />
                    ) : (
                      <Play className="w-5 h-5 text-white ml-0.5" />
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* Audio Script Display */}
            <div className="space-y-2 max-h-48 overflow-y-auto scrollbar-hide">
              {selectedDay.audio_script?.map((line, i) => (
                <div
                  key={i}
                  className={`p-3 rounded-lg transition-all duration-500 ${
                    currentLine === i
                      ? 'bg-amber-500/30 border border-amber-400/50 scale-[1.02]'
                      : currentLine > i 
                      ? 'bg-white/5 text-white/50'
                      : 'bg-white/5 text-white/70'
                  }`}
                >
                  <p className={`text-sm ${currentLine === i ? 'text-white font-medium' : ''}`}>
                    {line}
                  </p>
                </div>
              ))}
              
              {/* Closing Message */}
              {selectedDay.audio_closing && (
                <div
                  className={`p-4 mt-4 rounded-lg transition-all duration-500 border-2 ${
                    audioPhase === 'closing' || audioPhase === 'complete'
                      ? 'bg-amber-500/20 border-amber-400/50'
                      : 'bg-white/5 border-transparent text-white/40'
                  }`}
                >
                  <p className={`text-center font-medium ${
                    audioPhase === 'closing' || audioPhase === 'complete' 
                      ? 'text-amber-200' 
                      : ''
                  }`}>
                    {selectedDay.audio_closing}
                  </p>
                </div>
              )}
            </div>

            {audioPhase === 'complete' && (
              <div className="mt-4 text-center">
                <p className="text-emerald-400 text-sm font-medium flex items-center justify-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  Audio Complete
                </p>
              </div>
            )}
          </div>

          {/* Complete Day Button */}
          <button
            onClick={completeDay}
            disabled={!taskCompleted}
            className="w-full py-4 rounded-full bg-gradient-to-r from-amber-500 to-amber-700
                     text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed
                     hover:opacity-90 transition-opacity shadow-lg"
            data-testid="complete-day-btn"
          >
            Complete Day {selectedDay.day}
          </button>
        </div>

        {/* Audio Engine Overlay */}
        {showAudioEngine && sessionConfig && (
          <AudioEngine
            sessionConfig={sessionConfig}
            affirmations={selectedDay.audio_script || []}
            challengeContext={{
              day_title: selectedDay.title,
              theme: selectedDay.theme,
              closing_message: selectedDay.audio_closing
            }}
            onPhaseChange={(phase) => console.log('Phase:', phase)}
            onComplete={() => {
              setShowAudioEngine(false);
              // Mark audio as complete
              setAudioPhase('complete');
            }}
            onClose={() => setShowAudioEngine(false)}
          />
        )}

        {/* Share Modal */}
        <ShareModal
          isOpen={showShareModal}
          onClose={() => {
            setShowShareModal(false);
            // If it was a completion share, go back to challenge list
            if (shareType === 'completion' || shareType === 'achievement') {
              setSelectedDay(null);
            }
          }}
          shareData={shareData}
          type={shareType}
        />
      </div>
    );
  }

  // Challenge detail view
  if (selectedChallenge) {
    const progress = selectedChallenge.user_progress;
    const completedDays = progress?.completed_days?.map(d => d.day) || [];

    return (
      <div className="min-h-screen bg-[#050402]">
        <div className="px-6 pt-12 pb-6">
          <div className="flex items-center justify-between mb-6">
            <button 
              onClick={() => setSelectedChallenge(null)}
              className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
              data-testid="back-to-challenges-btn"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Challenges</span>
            </button>
            
            <button
              onClick={openShareInvite}
              className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/20 
                       text-amber-300 text-sm hover:bg-amber-500/30 transition-colors"
              data-testid="invite-friends-btn"
            >
              <Share2 className="w-4 h-4" />
              <span>Invite</span>
            </button>
          </div>
          
          <h1 className="text-2xl font-bold text-white mb-1" data-testid="challenge-title">
            {selectedChallenge.name}
          </h1>
          {selectedChallenge.tagline && (
            <p className="text-amber-400 italic mb-2">{selectedChallenge.tagline}</p>
          )}
          <p className="text-slate-400">{selectedChallenge.description}</p>
          
          {selectedChallenge.outcome && (
            <div className="mt-4 p-4 bg-gradient-to-r from-amber-500/10 to-amber-700/10 rounded-xl border border-amber-500/20">
              <p className="text-sm text-amber-300 italic">
                "{selectedChallenge.outcome}"
              </p>
            </div>
          )}
          
          {progress?.is_active && (
            <div className="mt-4 flex items-center gap-2 text-emerald-400 text-sm">
              <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span>In Progress - Day {progress.current_day}</span>
            </div>
          )}
        </div>

        <div className="px-6 pb-24">
          {/* Progress Bar */}
          {progress && (
            <div className="mb-6">
              <div className="flex justify-between text-sm text-slate-400 mb-2">
                <span>Progress</span>
                <span>{completedDays.length} / {selectedChallenge.duration_days} days</span>
              </div>
              <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-amber-400 to-amber-600 transition-all duration-500"
                  style={{ width: `${(completedDays.length / selectedChallenge.duration_days) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* Start Button */}
          {!progress?.is_active && completedDays.length === 0 && (
            <button
              onClick={() => startChallenge(selectedChallenge.id)}
              className="w-full py-4 rounded-full bg-gradient-to-r from-amber-500 to-amber-700
                       text-white font-medium mb-6 hover:opacity-90 transition-opacity shadow-lg"
              data-testid="start-challenge-btn"
            >
              Start Challenge
            </button>
          )}

          {/* Days List */}
          <div className="space-y-3">
            {selectedChallenge.days.map((day) => {
              const isCompleted = completedDays.includes(day.day);
              const isCurrent = progress?.current_day === day.day && progress?.is_active;
              const isLocked = !progress?.is_active || day.day > progress?.current_day;

              return (
                <button
                  key={day.day}
                  onClick={() => !isLocked && setSelectedDay(day)}
                  disabled={isLocked}
                  className={`w-full p-4 rounded-xl border text-left transition-all ${
                    isCompleted 
                      ? 'bg-emerald-500/10 border-emerald-500/30' 
                      : isCurrent
                      ? 'bg-amber-500/10 border-amber-500/50'
                      : isLocked
                      ? 'bg-white/5 border-white/5 opacity-50 cursor-not-allowed'
                      : 'bg-white/5 border-white/10 hover:bg-white/10'
                  }`}
                  data-testid={`day-${day.day}-btn`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      isCompleted 
                        ? 'bg-emerald-500' 
                        : isCurrent
                        ? 'bg-gradient-to-br from-amber-500 to-amber-700'
                        : 'bg-white/10'
                    }`}>
                      {isCompleted ? (
                        <Check className="w-5 h-5 text-white" />
                      ) : isLocked ? (
                        <Lock className="w-4 h-4 text-slate-500" />
                      ) : (
                        <span className="text-white font-bold">{day.day}</span>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className={`font-medium ${isLocked ? 'text-slate-500' : 'text-white'}`}>
                        {day.title}
                      </p>
                      <p className={`text-sm ${isLocked ? 'text-slate-600' : 'text-slate-400'}`}>
                        {day.theme}
                      </p>
                    </div>
                    {!isLocked && !isCompleted && (
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    )}
                    {isCurrent && (
                      <span className="text-xs bg-amber-500/30 text-amber-300 px-2 py-1 rounded-full">
                        Today
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Share Modal for Invite */}
        <ShareModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          shareData={shareData}
          type={shareType}
        />
      </div>
    );
  }

  // Challenges list view
  return (
    <div className="min-h-screen bg-[#050402]">
      <div className="px-6 pt-12 pb-6">
        <button 
          onClick={() => navigate('/networker')}
          className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-6"
          data-testid="back-to-networker-btn"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back</span>
        </button>
        <h1 className="text-2xl font-bold text-white mb-2">Challenges</h1>
        <p className="text-slate-400 text-sm">Multi-day guided programs for transformation</p>
      </div>

      <div className="px-6 space-y-4 pb-24">
        {challenges.map((challenge) => {
          const Icon = iconMap[challenge.icon] || Target;
          const progress = challenge.user_progress;
          const completedDays = progress?.completed_days?.length || 0;
          const isFree = challenge.is_free === true;
          const hasAccess = isFree || userHasPremium;

          return (
            <button
              key={challenge.id}
              onClick={() => hasAccess ? setSelectedChallenge(challenge) : null}
              className={`w-full bg-white/5 backdrop-blur-sm rounded-2xl p-5 border border-white/10 
                       transition-all duration-300 text-left group
                       ${hasAccess ? 'hover:bg-white/10' : 'opacity-80 cursor-default'}`}
              data-testid={`challenge-${challenge.id}-card`}
            >
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 relative transition-transform duration-300 group-hover:scale-105"
                     style={{
                       background: 'linear-gradient(135deg, rgba(217,165,90,0.32), rgba(217,165,90,0.08))',
                       boxShadow: '0 8px 22px -10px rgba(217,165,90,0.55), inset 0 1px 0 rgba(255,255,255,0.06)',
                       border: '1px solid rgba(217,165,90,0.22)',
                     }}>
                  <Icon className="w-7 h-7 text-amber-200/95" strokeWidth={1.6} />
                  {!hasAccess && (
                    <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center">
                      <Lock className="w-3 h-3 text-white" />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg font-semibold text-white">{challenge.name}</h3>
                    {isFree ? (
                      <span className="text-[10px] uppercase tracking-wider bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full font-medium">
                        Free
                      </span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-wider bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
                        <Crown className="w-3 h-3" />
                        Premium
                      </span>
                    )}
                    <div className="flex-1" />
                    {hasAccess && <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-white transition-colors" />}
                  </div>
                  {challenge.tagline && (
                    <p className={`text-sm italic mb-1 ${hasAccess ? 'text-amber-300' : 'text-amber-400/60'}`}>
                      {challenge.tagline}
                    </p>
                  )}
                  <p className={`text-sm mb-3 ${hasAccess ? 'text-slate-400' : 'text-slate-500'}`}>
                    {challenge.description}
                  </p>
                  
                  {progress?.is_active ? (
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-amber-400 to-amber-600"
                          style={{ width: `${(completedDays / challenge.duration_days) * 100}%` }}
                        />
                      </div>
                      <span className="text-xs text-slate-400">
                        {completedDays}/{challenge.duration_days}
                      </span>
                    </div>
                  ) : hasAccess ? (
                    <span className="text-xs px-2 py-1 rounded-full bg-white/10 text-slate-300">
                      {challenge.duration_days} days
                    </span>
                  ) : (
                    <span className="text-xs text-amber-400/80">
                      Upgrade to Premium to unlock
                    </span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default NetworkerChallengesPage;
